import "./App.css";
import {
  Button,
  Card,
  ColorPicker,
  Divider,
  Flex,
  Form,
  Input,
  message,
  Modal,
  QRCode,
  Segmented,
  Skeleton,
  Space,
  List,
  Typography,
  Upload,
  Empty,
} from "antd";
import useSWR, { useSWRConfig } from "swr";
import cookie from "react-cookies";
import COS from "cos-js-sdk-v5";

import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  HolderOutlined,
  InboxOutlined,
  LeftCircleOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import type { GetProps, QRCodeProps } from "antd";
import TextArea from "antd/es/input/TextArea";
import { UploadProps } from "antd/lib";
import Dragger from "antd/es/upload/Dragger";
import { SyntheticListenerMap } from "@dnd-kit/core/dist/hooks/utilities";
import {
  DndContext,
  DragEndEvent,
  DraggableAttributes,
  UniqueIdentifier,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import ImgCrop from "antd-img-crop";

const { Title } = Typography;

interface SortableListItemContextProps {
  setActivatorNodeRef?: (element: HTMLElement | null) => void;
  listeners?: SyntheticListenerMap;
  attributes?: DraggableAttributes;
}

const SortableListItemContext = createContext<SortableListItemContextProps>({});

const DragHandle: React.FC = () => {
  const { setActivatorNodeRef, listeners, attributes } = useContext(
    SortableListItemContext
  );
  return (
    <Button
      type="text"
      size="small"
      icon={<HolderOutlined />}
      style={{ cursor: "move" }}
      ref={setActivatorNodeRef}
      {...attributes}
      {...listeners}
    />
  );
};

const SortableListItem: React.FC<
  GetProps<typeof List.Item> & { itemKey: number }
> = (props) => {
  const { itemKey, style, ...rest } = props;

  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: itemKey });

  const listStyle: React.CSSProperties = {
    ...style,
    transform: CSS.Translate.toString(transform),
    transition,
    ...(isDragging ? { position: "relative", zIndex: 9999 } : {}),
  };

  const memoizedValue = useMemo<SortableListItemContextProps>(
    () => ({ setActivatorNodeRef, listeners, attributes }),
    [setActivatorNodeRef, listeners, attributes]
  );

  return (
    <SortableListItemContext.Provider value={memoizedValue}>
      <List.Item {...rest} ref={setNodeRef} style={listStyle} />
    </SortableListItemContext.Provider>
  );
};

const fetcher = async (url: string) => {
  const token = cookie.load("token");
  const res = await fetch(url, {
    headers: new Headers({
      "Access-Control-Allow-Origin": "*",
      Authorization: `Bearer ${token}`,
    }),
  });
  const json = await res.json();
  if (!json.success) {
    const error = new Error((await json).message);
    throw error;
  }
  return json;
};

const VoiceManage = () => {
  const { cid } = useParams();
  const { mutate } = useSWRConfig();
  const [addVoiceForm] = Form.useForm();
  const [editVoiceForm] = Form.useForm();
  const [addVoiceOpen, setAddVoiceOpen] = useState(false);
  const [deleteVoiceOpen, setDeleteVoiceOpen] = useState(false);
  const [editVoiceOpen, setEditVoiceOpen] = useState(false);
  const [editVoice, setEditVoice] = useState<any>({});
  const [deleteVoiceId, setDeleteVoiceId] = useState(0);
  const [createImage, setCreateImage] = useState("");
  const token = cookie.load("token");

  const [data, setData] = useState<any>([]);

  const setNewOrder = async (newArray: any[]) => {
    const newOrder = newArray.map((item: { id: number }, index: number) => ({
      id: item.id,
      order: index,
    }));
    const apiResponse = await (
      await fetch("https://api.lynne.com.cn/voice/setOrder", {
        method: "POST",
        body: JSON.stringify({
          orders: newOrder,
        }),
        headers: new Headers({
          "Access-Control-Allow-Origin": "*",
          Authorization: `Bearer ${token}`,
          "Content-type": "Application/json",
        }),
      })
    ).json();
    if (apiResponse.success) {
      message.success(apiResponse.message);
      mutate(`https://api.lynne.com.cn/voice/list?category_id=${cid}`);
    } else {
      message.error(apiResponse.message);
    }
  };

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (!active || !over) {
      return;
    }
    if (active.id !== over.id) {
      setData((prevState: any[]) => {
        const activeIndex = prevState.findIndex(
          (i: { id: UniqueIdentifier }) => i.id === active.id
        );
        const overIndex = prevState.findIndex(
          (i: { id: UniqueIdentifier }) => i.id === over.id
        );
        const newArray = arrayMove(prevState, activeIndex, overIndex);
        setNewOrder(newArray);
        return newArray;
      });
    }
  };

  const onCreateVoice = async (values: any) => {
    setAddVoiceOpen(false);
    const image = createImage !== "" ? createImage : null;
    const file = values.files.file.response.Location.replace(
      "lynne-1252518086.cos.ap-shanghai.myqcloud.com/",
      ""
    );
    const text = values.text ? values.text : null;
    const lrc = values.lrc ? values.lrc : null;
    const apiResponse = await (
      await fetch("https://api.lynne.com.cn/voice/create", {
        method: "POST",
        body: JSON.stringify({
          title: values.title,
          text,
          file,
          image,
          lrc,
          category_id: cid,
        }),
        headers: new Headers({
          "Access-Control-Allow-Origin": "*",
          Authorization: `Bearer ${token}`,
          "Content-type": "Application/json",
        }),
      })
    ).json();
    if (apiResponse.success) {
      message.success(apiResponse.message);
      mutate(`https://api.lynne.com.cn/voice/list?category_id=${cid}`);
    } else {
      message.error(apiResponse.message);
    }
    setCreateImage("");
  };

  const onEditVoice = async (values: any) => {
    setEditVoiceOpen(false);
    console.log(values);
    const image = createImage !== "" ? createImage : null;
    const file = values.files
      ? values.files.file.response.Location.replace(
          "lynne-1252518086.cos.ap-shanghai.myqcloud.com/",
          ""
        )
      : editVoice.file;
    const text = values.text ? values.text : null;
    const lrc = values.lrc ? values.lrc : null;
    const apiResponse = await (
      await fetch("https://api.lynne.com.cn/voice/edit", {
        method: "POST",
        body: JSON.stringify({
          id: editVoice.id,
          title: values.title,
          text,
          file,
          image,
          lrc,
        }),
        headers: new Headers({
          "Access-Control-Allow-Origin": "*",
          Authorization: `Bearer ${token}`,
          "Content-type": "Application/json",
        }),
      })
    ).json();
    if (apiResponse.success) {
      message.success(apiResponse.message);
      mutate(`https://api.lynne.com.cn/voice/list?category_id=${cid}`);
    } else {
      message.error(apiResponse.message);
    }

    setCreateImage("");
  };

  const onDeleteVoice = async () => {
    setDeleteVoiceOpen(false);
    const apiResponse = await (
      await fetch("https://api.lynne.com.cn/voice/delete", {
        method: "POST",
        body: JSON.stringify({
          id: deleteVoiceId,
        }),
        headers: new Headers({
          "Access-Control-Allow-Origin": "*",
          Authorization: `Bearer ${token}`,
          "Content-type": "Application/json",
        }),
      })
    ).json();
    if (apiResponse.success) {
      message.success(apiResponse.message);
      mutate(`https://api.lynne.com.cn/voice/list?category_id=${cid}`);
    } else {
      message.error(apiResponse.message);
    }
  };

  const voiceProps: UploadProps = {
    name: "file",
    multiple: false,
    action: "upload",
    showUploadList: { showDownloadIcon: true },
    maxCount: 1,
    async customRequest(options) {
      const cos = new COS({
        // getAuthorization 必选参数
        getAuthorization: function (options, callback) {
          const url = "https://api.lynne.com.cn/wiki/uploadFile"; // url 替换成您自己的后端服务
          const xhr = new XMLHttpRequest();
          xhr.open("GET", url, true);
          xhr.onload = function (e) {
            let data = null;
            let credentials = null;
            try {
              data = JSON.parse((e.target as XMLHttpRequest).responseText);
              credentials = data.credentials;
            } catch (e) {}
            if (!data || !credentials) {
              return console.error(
                "credentials invalid:\n" + JSON.stringify(data, null, 2)
              );
            }
            callback({
              TmpSecretId: credentials.tmpSecretId,
              TmpSecretKey: credentials.tmpSecretKey,
              SecurityToken: credentials.sessionToken,
              // 建议返回服务器时间作为签名的开始时间，避免用户浏览器本地时间偏差过大导致签名错误
              StartTime: data.startTime, // 时间戳，单位秒，如：1580000000
              ExpiredTime: data.expiredTime, // 时间戳，单位秒，如：1580000000
            });
          };
          xhr.send();
        },
      });
      cos.putObject(
        {
          Bucket: "lynne-1252518086", // 必须
          Region: "ap-shanghai", // 存储桶所在地域，必须字段
          Key: `file${Date.now()}-${(options.file as File).name}`, // 必须
          StorageClass: "STANDARD",
          Body: options.file, // 上传文件对象
          onProgress: (progressData) => {
            const progress = {
              percent: progressData.percent * 100,
            };
            options.onProgress && options.onProgress(progress);
          },
        },
        function (err, data) {
          if (err) {
            // 处理请求出错
            console.log(err);
          } else {
            // 处理请求成功
            console.log(data);
            options.onSuccess && options.onSuccess(data);
          }
        }
      );
    },
    onChange(info) {
      const { status } = info.file;
      if (status !== "uploading") {
        console.log(info.file, info.fileList);
      }
      if (status === "done") {
        message.success(`${info.file.name} 上传成功.`);
      } else if (status === "error") {
        message.error(`${info.file.name} 上传失败.`);
      }
    },
  };

  const props: UploadProps = {
    name: "file",
    action: "https://nekoda.cn/upload.php",
    headers: {
      authorization: "authorization-text",
    },
    maxCount: 1,
    onChange(info) {
      if (info.file.status === "done") {
        setCreateImage(
          `https://nekoda.cn/usr/uploads/${info.file.response.name}`
        );
        message.success("图片上传成功");
      } else if (info.file.status === "error") {
        message.error(`图片上传失败`);
      }
    },
    onRemove() {
      setCreateImage("");
    },
  };

  const {
    data: voiceData,
    error: voiceError,
    isLoading: voiceIsLoading,
  } = useSWR(`https://api.lynne.com.cn/voice/list?category_id=${cid}`, fetcher);
  const n = useNavigate();

  useEffect(() => {
    if (voiceData && voiceData.data) {
      setData(voiceData.data);
    }
  }, [voiceData]);
  if (voiceIsLoading || voiceData === undefined) return <Skeleton active />;
  if (voiceError) {
    cookie.remove("token");
    n("/login");
  }

  // 渲染数据
  return (
    <>
      <Modal
        open={addVoiceOpen}
        title="添加音频"
        okText="添加"
        cancelText="取消"
        okButtonProps={{ autoFocus: true, htmlType: "submit" }}
        onCancel={() => setAddVoiceOpen(false)}
        destroyOnClose
        maskClosable={false}
        modalRender={(dom) => (
          <Form
            layout="vertical"
            form={addVoiceForm}
            name="addSong"
            clearOnDestroy
            onFinish={(values) => onCreateVoice(values)}
          >
            {dom}
          </Form>
        )}
      >
        <Form.Item
          name="title"
          label="标题"
          rules={[
            {
              required: true,
              message: "请输入音频标题",
            },
          ]}
        >
          <Input autoComplete="off" />
        </Form.Item>
        <Form.Item
          name="files"
          label="音频文件"
          valuePropName="file"
          rules={[
            {
              required: true,
              message: "请上传音声",
            },
          ]}
        >
          <Dragger {...voiceProps}>
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">点击此处或拖动文件到此处上传</p>
          </Dragger>
        </Form.Item>
        <Form.Item name="text" label="文本（可选，这个是静态的喵）">
          <TextArea />
        </Form.Item>
        <Form.Item name="lrc" label="lrc格式歌词（可选，这个交给拾雨打轴吧~）">
          <TextArea />
        </Form.Item>
        <Form.Item label="封面（可选）" name="image" valuePropName="file">
          <ImgCrop rotationSlider quality={1}>
            <Upload {...props} listType="picture-card" maxCount={1}>
              <button style={{ border: 0, background: "none" }} type="button">
                <PlusOutlined />
              </button>
            </Upload>
          </ImgCrop>
        </Form.Item>
      </Modal>
      <Modal
        open={editVoiceOpen}
        title="编辑音频"
        okText="编辑"
        cancelText="取消"
        okButtonProps={{ autoFocus: true, htmlType: "submit" }}
        onCancel={() => setEditVoiceOpen(false)}
        destroyOnClose
        maskClosable={false}
        modalRender={(dom) => {
          let initial = { ...editVoice };
          editVoiceForm.setFieldsValue(initial);
          return (
            <Form
              layout="vertical"
              form={editVoiceForm}
              name="editSong"
              clearOnDestroy
              onFinish={(values) => onEditVoice(values)}
            >
              {dom}
            </Form>
          );
        }}
      >
        <Form.Item
          name="title"
          label="标题"
          rules={[
            {
              required: true,
              message: "请输入音声标题",
            },
          ]}
        >
          <Input autoComplete="off" />
        </Form.Item>
        <Form.Item name="files" label="音频文件" valuePropName="file">
          <Dragger
            {...voiceProps}
            defaultFileList={[
              {
                uid: "1",
                name: `${editVoice.file}`,
                status: "done",
                url: `https://dd.lynne.com.cn/file/${editVoice.file}`,
              },
            ]}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">点击此处或拖动文件到此处上传</p>
          </Dragger>
        </Form.Item>
        <Form.Item name="text" label="文本（可选，这个是静态的喵）">
          <TextArea />
        </Form.Item>
        <Form.Item name="lrc" label="lrc格式歌词（可选，这个交给拾雨打轴吧~）">
          <TextArea />
        </Form.Item>
        <Form.Item label="封面（可选）" name="image" valuePropName="file">
          <ImgCrop rotationSlider quality={1}>
            <Upload
              {...props}
              listType="picture-card"
              maxCount={1}
              defaultFileList={
                editVoice.image
                  ? [
                      {
                        uid: "2",
                        name: `封面`,
                        status: "done",
                        url: `${editVoice.image}`,
                      },
                    ]
                  : []
              }
            >
              <button style={{ border: 0, background: "none" }} type="button">
                <PlusOutlined />
              </button>
            </Upload>
          </ImgCrop>
        </Form.Item>
      </Modal>
      <Modal
        open={deleteVoiceOpen}
        title="确认删除喵？"
        okText="删除"
        okType="danger"
        cancelText="取消"
        onCancel={() => setDeleteVoiceOpen(false)}
        onOk={() => onDeleteVoice()}
        destroyOnClose
      >
        这个音声会被删除喵！
      </Modal>
      <Flex align="center" gap={10}>
        <Button
          type="primary"
          shape="circle"
          icon={<LeftCircleOutlined />}
          onClick={() => n("/dashboard/voiceCategory")}
        />
        <Title level={3} style={{ margin: "15px 0", flex: 1 }}>
          音频管理（可以拖动调整顺序喔）
        </Title>
      </Flex>
      <Flex align="center" gap={10}>
        <Button
          type="primary"
          onClick={() => {
            setCreateImage("");
            setAddVoiceOpen(true);
          }}
        >
          添加音频
        </Button>
      </Flex>
      <Divider />
      {voiceData.data.length !== 0 && (
        <DndContext
          modifiers={[restrictToVerticalAxis]}
          onDragEnd={onDragEnd}
          id="list-drag-sorting-handler"
        >
          <SortableContext
            items={data.map((item: { id: any }) => item.id)}
            strategy={verticalListSortingStrategy}
          >
            <List
              split={false}
              dataSource={data}
              renderItem={(item: any, index: number) => (
                <SortableListItem key={item.id} itemKey={item.id}>
                  <Card bordered={false} style={{ width: "100%" }}>
                    <Flex align="center" gap={10} wrap>
                      <DragHandle />
                      <Space style={{ flex: 1 }}>
                        {`${index + 1}. ${item.title}`}
                      </Space>
                      <Button
                        type="primary"
                        onClick={() => {
                          setEditVoice(item);
                          setCreateImage(item.image);
                          setEditVoiceOpen(true);
                        }}
                      >
                        编辑音声
                      </Button>
                      <Button
                        type="primary"
                        danger
                        onClick={() => {
                          setDeleteVoiceId(item.id);
                          setDeleteVoiceOpen(true);
                        }}
                      >
                        删除音声
                      </Button>
                    </Flex>
                  </Card>
                </SortableListItem>
              )}
            />
          </SortableContext>
        </DndContext>
      )}
      <Space direction="vertical" size={"middle"} style={{ width: "100%" }}>
        {voiceData.data.length === 0 && (
          <Empty
            styles={{ image: { height: 60 } }}
            description={"还没有音声喔"}
          >
            <Button
              type="primary"
              onClick={() => {
                setCreateImage("");
                setAddVoiceOpen(true);
              }}
            >
              添加一个~！
            </Button>
          </Empty>
        )}
      </Space>
    </>
  );
};

export default VoiceManage;
